/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import logo from "../../assets/images/pebble-logo.png";
import illustrator from "../../assets/images/illustration_container.svg";
import google_icon from "../../assets/images/google_icon.svg";
import { CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { authService } from "../../context/AuthService";
import PopupModalCreate from "./PopUpModalCreate";
import { useBusinessProfile } from "../../context/BusinessProfileContext";
import checkmark from "../../assets/images/checkmark-circle-outline.svg";
import checkmarkgreen from "../../assets/images/checkmark-circle.svg";
import illustratorMPS from "../../assets/images/illustrator_mps.svg";
import alertCircleIcon from "../../assets/images/alert-circle.svg";
import { useTranslation } from "react-i18next";
import LegalModal from "../../components/ui/LegalModal";
import DOMAIN_CONFIG from "../../config/domains";

const redirectWithToken = async (path) => {
  // In development, just navigate directly - no cross-domain token exchange needed
  if (DOMAIN_CONFIG.isDevelopment()) {
    window.location.href = path;
    return;
  }
  
  // In production, use auth callback for cross-domain token exchange
  try {
    const tokenResult = await authService.getIdToken();
    if (tokenResult.success) {
      const callbackPath = `/auth/callback?next=${encodeURIComponent(path)}&authToken=${encodeURIComponent(tokenResult.idToken)}`;
      DOMAIN_CONFIG.redirectToApp(callbackPath);
    } else {
      DOMAIN_CONFIG.redirectToApp(path);
    }
  } catch (error) {
    console.error('Error getting token for redirect:', error);
    DOMAIN_CONFIG.redirectToApp(path);
  }
};

const CreateAccount = () => {
  const { t } = useTranslation();
  const { user, signUp, signUpWithGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [userType, setUserType] = useState(() => {
    return location.state?.userType || "EO";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userSignedUp, setUserSignedUp] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralStatus, setReferralStatus] = useState(null);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [passwordConditions, setPasswordConditions] = useState({
    capital: false,
    length: false,
    symbol: false,
  });
  const [eventTypeIndex, setEventTypeIndex] = useState(0);
  const [accountExists, setAccountExists] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const eventTypeKeys = [
    "festival", "farmersMarket", "fleaMarket", "artisanMarket", "popUp",
    "vendorFair", "marketplace", "shoppingSquare", "concertConcessions",
    "sportingEventConcessions", "artFair", "merchStands"
  ];

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const { updateBusinessProfile } = useBusinessProfile();
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });

  const handleClose = () => setOpenConfirmation(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (userSignedUp && user && userType === "EO") {
      handleClose();
      setIsTransitioning(true);
      // Small delay to ensure smooth transition
      setTimeout(() => {
        redirectWithToken("/eo-paywall");
      }, 100);
    }
    if (userSignedUp && user && userType === "MPS") {
      handleClose();
      setIsTransitioning(true);
      setTimeout(() => {
        redirectWithToken("/mps-signup");
      }, 100);
    }
  }, [user, userSignedUp, navigate, userType]);

  useEffect(() => {
    setPasswordConditions({
      capital: /[A-Z]/.test(password),
      length: password.length >= 8,
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [password]);

  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;
    
    setIsCheckingEmail(true);
    try {
      const result = await authService.checkEmailExists(email);
      setAccountExists(result.exists);
      if (result.exists) {
        setPassword('');
      }
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (accountExists) {
      setAccountExists(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setEventTypeIndex((prev) => (prev + 1) % eventTypeKeys.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [eventTypeKeys.length]);

  useEffect(() => {
    const refCode = searchParams.get("ref") || localStorage.getItem("referralCode");
    const eventId = searchParams.get("event") || localStorage.getItem("referralEventId");
    
    if (refCode && !referralCode) {
      setReferralCode(refCode);
      setUserType("MPS");
      
      const validateCode = async () => {
        setIsValidatingCode(true);
        try {
          const result = await authService.validateReferralCode(refCode);
          setReferralStatus(result);
          if (result.success) {
            localStorage.setItem("referralCode", refCode);
            localStorage.setItem("referrerId", result.referrerId);
            if (eventId) {
              localStorage.setItem("referralEventId", eventId);
            }
          }
        } catch (error) {
          setReferralStatus({ success: false, message: "Invalid referral code." });
        } finally {
          setIsValidatingCode(false);
        }
      };
      validateCode();
    }
  }, [searchParams, referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }
    
    if (accountExists) {
      // Login existing user
      setIsLoading(true);
      try {
        const result = await authService.signInExistingUser(email, password);
        if (result.success) {
          if (rememberMe) {
            localStorage.setItem('rememberedEmail', email);
          }
          setUserSignedUp(true);
        } else {
          alert(result.message);
        }
      } catch (error) {
        alert('Login failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // Create new account
      if (passwordConditions.capital && passwordConditions.length && passwordConditions.symbol) {
        setOpenConfirmation(true);
      }
    }
  };

  const handleValidateReferral = async () => {
    if (!referralCode) return;
    setIsValidatingCode(true);
    try {
      const result = await authService.validateReferralCode(referralCode);
      setReferralStatus(result);
      if (result.success) {
        localStorage.setItem('referralCode', referralCode);
        localStorage.setItem('referrerId', result.referrerId);
      }
    } catch (error) {
      setReferralStatus({ success: false, message: "Invalid referral code." });
    } finally {
      setIsValidatingCode(false);
    }
  };

  const handleCreateAccount = async () => {
    setIsLoading(true);
    try {
      const storedReferralCode = localStorage.getItem('referralCode');
      const storedReferrerId = localStorage.getItem('referrerId');
      const response = await signUp(email, password, userType, {
        referralCode: storedReferralCode,
        referrerId: storedReferrerId
      });
      
      localStorage.removeItem('referralCode');
      localStorage.removeItem('referrerId');
      
      if (!response.success) {
        setIsLoading(false);
        setOpenConfirmation(false);
        
        // If account requires Google sign-in, trigger it automatically
        if (response.requiresGoogleSignIn) {
          handleGoogleSignUp();
          return;
        }
        
        alert(response.message || 'Failed to create account. Please try again.');
        return;
      }
      
      if (response.autoLoggedIn && response.isSignedUp) {
        // User already had an account and was auto-logged in - redirect to dashboard
        setIsTransitioning(true);
        sessionStorage.setItem('showWelcomeBack', 'true');
        if (userType === "EO") {
          await redirectWithToken("/eo/dashboard");
        } else {
          await redirectWithToken("/mps/dashboard");
        }
      } else {
        setUserSignedUp(true);
      }
    } catch (error) {
      console.error("Error creating account:", error);
      setIsLoading(false);
      setOpenConfirmation(false);
      alert('An error occurred while creating your account. Please try again.');
    }
  };

  const handleGoogleSignUp = async () => {
    if (!agreed) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
      return;
    }
    setIsLoading(true);
    try {
      const response = await signUpWithGoogle(userType);
      if (!response.success) {
        setIsLoading(false);
        alert('Sign up failed: ' + (response.message || 'Unknown error'));
        navigate("/login");
      } else {
        // Check if this is an existing user who is already fully signed up
        if (response.isSignedUp) {
          // Existing user - redirect directly to dashboard based on user type
          setIsTransitioning(true);
          sessionStorage.setItem('showWelcomeBack', 'true');
          if (userType === "EO") {
            await redirectWithToken("/eo/dashboard");
          } else {
            await redirectWithToken("/mps/dashboard");
          }
        } else {
          // New user or incomplete signup - continue with normal flow
          setUserSignedUp(true);
        }
      }
    } catch (error) {
      setIsLoading(false);
      alert('An error occurred. Please try again.');
    }
  };

  if (isTransitioning) {
    return (
      <div className="fixed inset-0 bg-grey-50 z-50 flex items-center justify-center animate-fade-in">
        <div className="flex flex-col items-center gap-4">
          <img className="h-10 w-auto" src={logo} alt="Pebble" />
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-pebble-red rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <p className="text-sm text-grey-500">Setting up your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grey-50 flex flex-col items-center justify-start sm:justify-center p-3 sm:p-4 pt-14 sm:pt-4 overflow-y-auto">
      {isLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin" />
        </div>
      )}
      <div className="fixed top-2 right-2 sm:absolute sm:top-3 sm:right-3 flex items-center gap-1.5 sm:gap-2 z-40">
        <Link
          to="/browse-events"
          className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-medium text-white bg-pebble-red shadow-md hover:bg-pebble-red/90 hover:shadow-lg transition-all duration-150 active:scale-[0.98] active:shadow-sm cursor-pointer"
        >
          {t('navigation.browseEvents')}
        </Link>
      </div>

      <Link to="/">
        <img className="h-7 sm:h-8 w-auto mb-3 sm:mb-4" src={logo} alt="Pebble" />
      </Link>

      <div className="bg-white shadow-lg rounded-2xl flex flex-col md:flex-row w-full max-w-[880px]">
        <div className="w-full md:w-1/2 p-4 sm:p-5">
          <h2 className="text-sm sm:text-base font-medium mb-0.5 text-center text-grey-600">
            {t('auth.signup.title')}
          </h2>
          <div className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-center">
            <span className="text-grey-900">{(() => {
              const eventTypeKey = eventTypeKeys[eventTypeIndex];
              const eventType = t(`eventTypes.${eventTypeKey}`);
              const pluralTypes = ['concertConcessions', 'sportingEventConcessions', 'merchStands'];
              if (pluralTypes.includes(eventTypeKey)) return t('auth.signup.manage');
              return /^[aeiouAEIOU]/.test(eventType) ? t('auth.signup.manageAn') : t('auth.signup.manageA');
            })()} </span>
            <span key={eventTypeIndex} className="text-pebble-red animate-fade-in">
              {t(`eventTypes.${eventTypeKeys[eventTypeIndex]}`)}
            </span>.
          </div>

          <div className="flex rounded-lg shadow-md mb-2 sm:mb-3 border border-grey-300 overflow-hidden">
            <button
              type="button"
              className={`${userType === "EO" ? "bg-primary-400 text-white shadow-inner" : "bg-white text-grey-900 hover:bg-grey-50"} w-1/2 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-l-lg cursor-pointer transition-all duration-150 active:scale-[0.98]`}
              onClick={() => setUserType("EO")}
            >
              {t('auth.userTypes.eventOrganizer')}
            </button>
            <button
              type="button"
              className={`${userType === "MPS" ? "bg-primary-400 text-white shadow-inner" : "bg-white text-grey-900 hover:bg-grey-50"} w-1/2 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-r-lg cursor-pointer transition-all duration-150 active:scale-[0.98]`}
              onClick={() => setUserType("MPS")}
            >
              {t('auth.userTypes.marketplaceSeller')}
            </button>
          </div>

          {userType === "EO" && (
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                placeholder={t('auth.signup.referralPlaceholder')}
                value={referralCode}
                onChange={(e) => { setReferralCode(e.target.value.trim().toUpperCase()); setReferralStatus(null); }}
                className={`flex-1 h-8 px-3 border rounded-lg text-sm ${
                  referralStatus?.success ? "border-green-500 bg-green-50" : referralStatus?.success === false ? "border-red-500" : "border-grey-300"
                }`}
              />
              {!referralStatus?.success && (
                <button
                  onClick={handleValidateReferral}
                  disabled={isValidatingCode || !referralCode}
                  className={`h-8 px-4 rounded-lg text-sm font-medium flex-shrink-0 transition-all duration-150 ${
                    referralCode && !isValidatingCode ? "bg-grey-700 text-white shadow-md hover:bg-grey-800 hover:shadow-lg active:scale-[0.98] active:shadow-sm cursor-pointer" : "bg-grey-200 text-grey-400 cursor-not-allowed"
                  }`}
                >
                  {isValidatingCode ? "..." : t('common.apply')}
                </button>
              )}
              {referralStatus?.success && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />}
              {referralStatus && !referralStatus.success && (
                <span className="text-xs text-red-500 flex-shrink-0">{referralStatus.message}</span>
              )}
            </div>
          )}

          <button
            type="button"
            className="w-full h-8 sm:h-9 flex justify-center items-center border border-grey-300 rounded-lg shadow-md bg-white text-xs sm:text-sm font-semibold hover:bg-grey-50 hover:shadow-lg mb-2 sm:mb-3 cursor-pointer transition-all duration-150 active:scale-[0.98] active:shadow-sm"
            onClick={handleGoogleSignUp}
          >
            <img className="h-4 w-4 mr-2" src={google_icon} alt="Google" />
            {t('auth.signup.signUpWithGoogle')}
          </button>

          <div className="relative mb-2 sm:mb-3 flex items-center">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="px-2 sm:px-3 text-[10px] sm:text-xs text-gray-500">{t('auth.signup.orSignUpWithEmail')}</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="block text-xs font-medium text-grey-700 mb-1">{t('auth.signup.emailLabel')}</label>
              <div className="relative">
                <input
                  type="email"
                  required
                  className={`w-full h-9 px-3 border rounded-lg text-sm ${
                    accountExists ? 'border-primary-400 bg-primary-25' : 'border-grey-300'
                  }`}
                  placeholder={t('auth.signup.emailPlaceholder')}
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                />
                {isCheckingEmail && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="text-xs text-grey-400">...</span>
                  </div>
                )}
              </div>
              {accountExists && (
                <p className="mt-1 text-xs text-primary-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Account found! Enter your password to sign in.
                </p>
              )}
            </div>

            <div className="mb-2">
              <label className="block text-xs font-medium text-grey-700 mb-1">{t('auth.signup.passwordLabel')}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  className={`w-full h-9 px-3 pr-9 border rounded-lg text-sm ${
                    password && passwordConditions.capital && passwordConditions.length && passwordConditions.symbol
                      ? "border-grey-300" : password ? "border-red-300 bg-red-50" : "border-grey-300"
                  }`}
                  placeholder={t('auth.signup.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {!accountExists && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[
                  { condition: "capital", text: t('auth.passwordRequirements.capital') },
                  { condition: "length", text: t('auth.passwordRequirements.length') },
                  { condition: "symbol", text: t('auth.passwordRequirements.symbol') },
                ].map(({ condition, text }) => (
                  <div
                    key={condition}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] ${
                      passwordConditions[condition] ? "bg-green-100 text-green-700" : password ? "bg-red-100 text-red-700" : "bg-grey-100 text-grey-600"
                    }`}
                  >
                    {passwordConditions[condition] ? (
                      <img src={checkmarkgreen} alt="" className="w-3 h-3" />
                    ) : password ? (
                      <img src={alertCircleIcon} alt="" className="w-3 h-3" />
                    ) : (
                      <img src={checkmark} alt="" className="w-3 h-3" />
                    )}
                    {text}
                  </div>
                ))}
              </div>
            )}

            {accountExists && (
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="rememberMe"
                  className="h-3.5 w-3.5 border-grey-300 rounded accent-pebble-red"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="rememberMe" className="ml-2 text-xs text-grey-700">
                  Remember me on this device
                </label>
              </div>
            )}

            <div className="relative flex items-center mb-3">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 border-grey-300 rounded accent-pebble-red"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <label className="ml-2 text-xs text-grey-700">
                I agree to the Pebble{" "}
                <Link to="/terms-of-use" className="text-pebble-red hover:underline">Terms of Use</Link>
                {" "}and{" "}
                <Link to="/privacy-policy" className="text-pebble-red hover:underline">Privacy Policy</Link>
              </label>
              {showTooltip && (
                <div className="absolute -top-8 left-0 bg-red-500 text-white text-xs px-2 py-1 rounded">
                  Please agree to terms
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={!email || !password || !agreed || isLoading || (!accountExists && !(passwordConditions.capital && passwordConditions.length && passwordConditions.symbol))}
              className={`w-full h-9 rounded-lg text-sm font-semibold transition-all duration-150 ${
                email && password && agreed && !isLoading && (accountExists || (passwordConditions.capital && passwordConditions.length && passwordConditions.symbol))
                  ? "bg-grey-700 text-white hover:bg-grey-800 shadow-md hover:shadow-lg active:scale-[0.98] active:shadow-sm cursor-pointer"
                  : "bg-grey-300 text-grey-500 cursor-not-allowed"
              }`}
            >
              {isLoading ? t('common.loading') : accountExists ? 'Sign In' : t('common.continue')}
            </button>
          </form>

          <p className="mt-2 text-center text-xs text-gray-600">
            Already have an account?{' '}
            <span className="text-pebble-red cursor-pointer hover:underline" onClick={() => navigate('/login', { state: { userType } })}>
              Sign in
            </span>
          </p>
        </div>

        <div className="hidden md:block w-1/2 bg-primary-primary-red rounded-r-2xl overflow-hidden">
          <img className="object-cover w-full h-full" src={userType === "EO" ? illustrator : illustratorMPS} alt="" />
        </div>
      </div>

      <div className="mt-2 sm:mt-3 text-center px-2 sm:px-0">
        <nav className="flex flex-wrap justify-center items-center gap-x-1 sm:gap-x-1.5 gap-y-0.5 text-[8px] sm:text-[9px] text-grey-500 mb-1.5 max-w-[280px] sm:max-w-none mx-auto">
          <Link to="/about" className="hover:text-pebble-red">About</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/careers" className="hover:text-pebble-red">Careers</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/browse-events" className="hover:text-pebble-red">Directory</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/partnerships" className="hover:text-pebble-red">Partnerships</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/faqs" className="hover:text-pebble-red">FAQs</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/host" className="hover:text-pebble-red">Host</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/contact" className="hover:text-pebble-red">Contact</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/invest" className="hover:text-pebble-red">Invest</Link>
          <span className="hidden sm:inline">·</span>
          <Link to="/support" className="hover:text-pebble-red">Support</Link>
        </nav>
        <div className="flex justify-center space-x-3 mb-1">
          <a href="https://www.facebook.com/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-pebble-red">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a href="https://www.instagram.com/pebbletheappofficial?igsh=MTB5cHk4aGJtdm54Zw==" target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-pebble-red">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/></svg>
          </a>
          <a href="https://www.linkedin.com/company/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-pebble-red">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
          </a>
          <a href="https://youtube.com/@techloungepodcast?si=FDYcAQBFKd7xg-BB" target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-pebble-red">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          </a>
          <a href="https://twitter.com/pebbletheapp" target="_blank" rel="noopener noreferrer" className="text-grey-400 hover:text-pebble-red">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </a>
        </div>
        <p className="text-[8px] text-grey-400 mt-4">
          © 2025 Pebble The App, Inc.
        </p>
      </div>

      <PopupModalCreate isOpen={openConfirmation} onClose={handleClose}>
        <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center">
          {isLoading ? (
            <div className="py-4 flex flex-col items-center">
              <p className="text-sm text-grey-600">Creating account...</p>
            </div>
          ) : (
            <>
              <h3 className="text-base font-semibold mb-2">Ready to create account?</h3>
              <p className="text-sm text-grey-600 mb-4">You'll receive a verification email at {email}</p>
              <div className="flex gap-3">
                <button className="flex-1 py-2 border border-grey-300 rounded-lg text-sm" onClick={handleClose}>
                  Cancel
                </button>
                <button className="flex-1 py-2 bg-grey-700 text-white rounded-lg text-sm font-medium" onClick={handleCreateAccount}>
                  Create
                </button>
              </div>
            </>
          )}
        </div>
      </PopupModalCreate>

      <LegalModal 
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
        type={legalModal.type}
      />
    </div>
  );
};

export default CreateAccount;
